import { Copy } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import {
  collectDiagnostics,
  formatDiagnosticsText,
} from '@/app/diagnostics/diagnostics'
import type { DiagnosticRow } from '@/app/diagnostics/types'
import { surfaceError } from '@/core/reporting'
import { TEST_IDS } from '@/lib/testIds'
import { useIsCoarsePointer } from '@/lib/usePointerType'
import { Button } from '@/ui/shadcn/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/ui/shadcn/dialog'

interface DiagnosticsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function DiagnosticsDialog({ open, onOpenChange }: DiagnosticsDialogProps) {
  const isCoarse = useIsCoarsePointer()
  const [rows, setRows] = useState<DiagnosticRow[] | null>(null)

  useEffect(() => {
    if (!open) return
    let active = true
    void collectDiagnostics().then((diagnostics) => {
      if (active) setRows(diagnostics)
    }).catch((error: unknown) => {
      surfaceError(error, 'Could not collect diagnostics.')
    })
    return () => {
      active = false
    }
  }, [open])

  useEffect(() => {
    if (isCoarse) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (!event.ctrlKey || !event.shiftKey || event.altKey || event.metaKey || event.code !== 'KeyD') return
      event.preventDefault()
      setRows(null)
      onOpenChange(true)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isCoarse, onOpenChange])

  const copy = async () => {
    if (!rows) throw new Error('Diagnostics cannot be copied before collection finishes')
    try {
      await navigator.clipboard.writeText(formatDiagnosticsText(rows))
      toast.success('Diagnostics copied')
    } catch (error) {
      surfaceError(error, 'Could not copy diagnostics.')
    }
  }

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => {
      if (nextOpen) setRows(null)
      onOpenChange(nextOpen)
    }}>
      <DialogContent
        data-testid={TEST_IDS.diagnosticsDialog}
        data-diagnostics-state={rows ? 'ready' : 'loading'}
        className="max-h-[calc(100dvh-2rem)] grid-rows-[auto_minmax(0,1fr)_auto]"
      >
        <DialogHeader>
          <DialogTitle>Diagnostics</DialogTitle>
          <DialogDescription>Local device capabilities. No photo or file data is included.</DialogDescription>
        </DialogHeader>
        {rows
          ? (
              <ul className="min-h-0 space-y-2 overflow-y-auto pr-1 text-sm">
                {rows.map((row) => (
                  <li key={row.id} data-diagnostic-row={row.id} className="leading-5">
                    <span className="font-medium">{row.name}</span>
                    <span aria-hidden="true"> · </span>
                    <span data-diagnostic-value>{row.value}</span>
                    <span aria-hidden="true"> · </span>
                    <span aria-label={row.ok ? 'available' : 'unavailable'}>{row.ok ? '✓' : '✗'}</span>
                  </li>
                ))}
              </ul>
            )
          : <p className="text-sm text-muted-foreground">Checking this device…</p>}
        <Button
          type="button"
          data-testid={TEST_IDS.diagnosticsCopy}
          disabled={!rows}
          onClick={() => void copy()}
          className="justify-self-start"
        >
          <Copy className="size-4" aria-hidden="true" />
          Copy
        </Button>
      </DialogContent>
    </Dialog>
  )
}
